"use client";

import { PRIVACY_POLICY_LAST_UPDATED, TERMS_OF_SERVICE_LAST_UPDATED } from "@/config/agreements";
import {
  useGetV2EmployeeAgreementsPrivacy,
  useGetV2EmployeeAgreementsTos,
} from "@/gen/orval/webplatform/endpoints/employee/employee.gen";
import { useGetV2Employees } from "@/gen/orval/webplatform/endpoints/employees/employees.gen";
import { useUser } from "@/lib/integrations/descope";
import { useJwt } from "@/subsystems/access/json-web-token/hooks/use-jwt";

export function useGetEmployee() {
  const employees = useGetV2Employees();

  // The server-verified session identifies the employee even when Descope's client-side refresh
  // fails, which otherwise leaves `user` empty and reads as an un-onboarded employee.
  const sessionAuthUserId = useJwt()?.user.authId;
  const { isUserLoading, user } = useUser();

  // Extract the conditional logic to help React Compiler optimize
  const userId = user?.userId;
  const userEmail = user?.email;

  const employeeData =
    employees.data && Array.isArray(employees.data)
      ? employees.data.find((employee) => {
          // the authUserId should match the sub from the object. But fallback to email.
          const authUserIdMatch =
            (sessionAuthUserId !== undefined && employee?.authUserId === sessionAuthUserId) ||
            employee?.authUserId === userId;
          const emailMatch = employee?.email === userEmail;
          return authUserIdMatch || emailMatch;
        })
      : null;

  return {
    employee: {
      ...employees,
      data: employeeData || null,
      isLoading: employees?.isLoading || (!employeeData && isUserLoading),
    },
    employees,
  };
}

export function useGetEmployeeAgreements() {
  const termsOfService = useGetV2EmployeeAgreementsTos();
  const privacyPolicy = useGetV2EmployeeAgreementsPrivacy();

  // Create an array of queries to check for loading state
  const queriesToCheck = [termsOfService, privacyPolicy];
  const isLoading = queriesToCheck.some((query) => query.isLoading);

  const termsOfServiceVerboseStatus = getStatus({
    acceptanceStatus: termsOfService?.data?.acceptanceStatus,
    compareLastAcceptedTo: TERMS_OF_SERVICE_LAST_UPDATED,
    isLoading,
    lastAccepted: termsOfService?.data?.lastAccepted,
  });
  const privacyPolicyVerboseStatus = getStatus({
    acceptanceStatus: privacyPolicy?.data?.acceptanceStatus,
    compareLastAcceptedTo: PRIVACY_POLICY_LAST_UPDATED,
    isLoading,
    lastAccepted: privacyPolicy?.data?.lastAccepted,
  });

  return {
    isLoading,
    privacyPolicy: {
      ...privacyPolicy,
      data: privacyPolicy.data
        ? {
            ...privacyPolicy.data,
            verboseStatus: privacyPolicyVerboseStatus,
          }
        : null,
    },
    termsOfService: {
      ...termsOfService,
      data: termsOfService.data
        ? {
            ...termsOfService.data,
            verboseStatus: termsOfServiceVerboseStatus,
          }
        : null,
    },
  };
}

function getStatus({
  acceptanceStatus,
  compareLastAcceptedTo,
  isLoading,
  lastAccepted,
}: {
  acceptanceStatus: boolean | null | undefined;
  compareLastAcceptedTo: string;
  isLoading: boolean;
  lastAccepted: null | string | undefined;
}): "accepted" | "loading" | "needs_reacceptance" | "not_accepted" {
  if (isLoading) {
    return "loading";
  }

  if (lastAccepted) {
    const lastAcceptedDate = new Date(lastAccepted);
    const compareDate = new Date(compareLastAcceptedTo);

    if (lastAcceptedDate < compareDate) {
      return "needs_reacceptance";
    }
  }

  return acceptanceStatus ? "accepted" : "not_accepted";
}
